// instrumentPrefsStore — instrument/display preference state.
//
// Extracted from uiStore.ts as step 2b of HI-2 (audit 2026-05-22).
// The 6 instrument/display pref fields + their 7 setters moved here so
// the uiStore can shrink one bucket at a time.
//
// Persistence: reads/writes the shared `fyrefly.ui.v1` localStorage
// blob (for showInstruments, instruments[], clock24h) plus the
// legacy per-key slots (fyrefly.instruments.v1 for instrument selection,
// fyrefly.gloveMode.v1 for glove mode, fyrefly.keepScreenAwake.v1 and
// fyrefly.highContrastInstruments.v1 via readBoolPref/persistBoolPref).
// No localStorage key namespace changes — existing users keep their
// persisted preferences after the upgrade.
//
// Cloud-sync: the PersistedUiPrefs schema in prefsSync.ts already holds
// showInstruments, instruments[], and clock24h. On pull (sign-in),
// prefsSync.ts applies those fields to this store. gloveMode /
// keepScreenAwake / highContrastInstruments are NOT in the cloud blob
// (they use their own localStorage keys only — intentional, same as before).
//
// clock24h is a special case: non-React modules (src/lib/timeFormat.ts)
// read clock preference via their own module-level variable that is kept
// in sync via setClock24hPref(). We call setClock24hPref() on
// initialization and inside setClock24h() so the non-React path always
// reflects the current preference — same guarantee as uiStore had.
//
// toggleInstrument has non-trivial logic: it enforces a 5-item cap by
// dropping the OLDEST selection when adding a 6th, and it skips Wave-2
// placeholder instruments that aren't available. Copied byte-for-byte
// from uiStore.ts.
//
// Uses subscribeWithSelector middleware so consumers can do fine-grained
// field-level subscriptions (audit synthesis HI-2 goal).

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { setClock24hPref } from '../lib/timeFormat';
import {
  DEFAULT_INSTRUMENTS,
  INSTRUMENT_BY_ID,
  MAX_INSTRUMENTS,
  type InstrumentId,
} from '../lib/instruments';
import {
  loadInstrumentSelection,
  saveInstrumentSelection,
  readGloveMode,
  persistGloveMode,
  applyGloveMode,
  readBoolPref,
  persistBoolPref,
  loadUiPrefs,
  persistUiPrefs,
} from './prefsSync';

// Re-export InstrumentId so consumers that previously imported it from
// uiStore can switch to this store without breaking their type imports.
export type { InstrumentId };

// ── Types ────────────────────────────────────────────────────────────

export interface InstrumentPrefsState {
  /** Bottom data-strip visibility on map. */
  showInstruments: boolean;
  toggleInstruments: (next?: boolean) => void;

  /** Glove-friendly UI: bumps tap-target sizes on every interactive
   *  control via the `data-glove` attribute on `<html>`. Pilots wearing
   *  fire gloves can't reliably hit standard 40px tap targets. */
  gloveMode: boolean;
  toggleGloveMode: (next?: boolean) => void;

  /** Keep the device screen on while the app is in the foreground.
   *  Uses the Screen Wake Lock API. Wiley's request 2026-05-11:
   *  pilots don't want the iPad to sleep mid-flight. */
  keepScreenAwake: boolean;
  toggleKeepScreenAwake: (next?: boolean) => void;

  /** High-contrast instrument bar (black bg, bold white text). Per
   *  Wiley feedback 2026-05-12: the dim 55%-opacity bar washes out
   *  in direct sunlight. */
  highContrastInstruments: boolean;
  toggleHighContrastInstruments: (next?: boolean) => void;

  /** Configurable instrument bar selection (max 5, persists to localStorage). */
  instruments: InstrumentId[];
  /** Replace the full selection. Caller is responsible for the 5-item cap. */
  setInstruments: (ids: InstrumentId[]) => void;
  /** Toggle a single id; if turning ON would exceed the cap, drops the
   *  oldest selection. Skips Wave-2 placeholders that aren't available. */
  toggleInstrument: (id: InstrumentId) => void;

  /** Display times in 24-hour clock format. Default true — the
   *  primary user is a pilot, METAR/TAF/ATIS/ATC are all 24-hour,
   *  and we already render Zulu in HHMM format. AM/PM in the
   *  cockpit creates an unnecessary mental flip. Cloud-synced via
   *  ui_prefs so the choice rides across devices. */
  clock24h: boolean;
  setClock24h: (v: boolean) => void;
}

// ── Initialization ───────────────────────────────────────────────────

// Hydrate from the shared prefs blob so the first render already
// reflects the user's saved choices (no default-then-snap flicker).
const _prefs = loadUiPrefs();

// Push the clock-format choice into timeFormat module state so the
// helper functions (formatLocalTime, formatZuluWithLocal, etc.)
// reflect the user's preference from the very first call. Without
// this the first render of any time string would use the default
// (24h) regardless of what the user had previously chosen.
setClock24hPref(_prefs.clock24h ?? true);

// ── Store ────────────────────────────────────────────────────────────

export const useInstrumentPrefsStore = create<InstrumentPrefsState>()(
  subscribeWithSelector((set, get) => ({
    showInstruments: _prefs.showInstruments ?? true,
    toggleInstruments: (next) => {
      const v = next ?? !get().showInstruments;
      set({ showInstruments: v });
      persistUiPrefs({ showInstruments: v });
    },

    gloveMode: readGloveMode(),
    toggleGloveMode: (next) => {
      const v = next ?? !get().gloveMode;
      set({ gloveMode: v });
      persistGloveMode(v);
      applyGloveMode(v);
    },

    // Hydration priority for cloud-synced flags: prefer the unified
    // `fyrefly.ui.v1` blob (which the cloud pull writes back into on
    // sign-in), then fall back to the legacy per-key LS slot, then
    // default. This way, when the user signs in on Device B and the
    // pull lands `keepScreenAwake: true` in the blob, a subsequent
    // reload (before the user touches the toggle again) restores it
    // — previously, init read only the per-key slot and got `false`
    // because the cloud pull never wrote it back there. Boss bug
    // report 2026-05-26 13:23 PT: "settings are still not persisting
    // between logins".
    keepScreenAwake:
      typeof _prefs.keepScreenAwake === 'boolean'
        ? _prefs.keepScreenAwake
        : readBoolPref('keepScreenAwake', false),
    toggleKeepScreenAwake: (next) => {
      const v = next ?? !get().keepScreenAwake;
      set({ keepScreenAwake: v });
      persistBoolPref('keepScreenAwake', v);
      // Mirror into the cloud-sync blob (added 2026-05-26 02:31 PT).
      persistUiPrefs({ keepScreenAwake: v });
    },

    highContrastInstruments:
      typeof _prefs.highContrastInstruments === 'boolean'
        ? _prefs.highContrastInstruments
        : readBoolPref('highContrastInstruments', false),
    toggleHighContrastInstruments: (next) => {
      const v = next ?? !get().highContrastInstruments;
      set({ highContrastInstruments: v });
      persistBoolPref('highContrastInstruments', v);
      persistUiPrefs({ highContrastInstruments: v });
    },

    instruments: loadInstrumentSelection(),
    setInstruments: (ids) => {
      // Cap at MAX, drop unknown / unavailable ids defensively.
      const valid = ids
        .filter((id) => {
          const def = INSTRUMENT_BY_ID[id];
          return !!def && def.available;
        })
        .slice(0, MAX_INSTRUMENTS);
      saveInstrumentSelection(valid);
      // Mirror into ui_prefs so the selection rides the cloud-sync blob
      // (survives logout / new browser / new device). Local LS key is
      // kept as a same-device cache + back-compat.
      persistUiPrefs({ instruments: valid });
      set({ instruments: valid });
    },
    toggleInstrument: (id) => {
      const def = INSTRUMENT_BY_ID[id];
      if (!def || !def.available) return;
      const cur = get().instruments;
      const has = cur.includes(id);
      let next: InstrumentId[];
      if (has) {
        next = cur.filter((x) => x !== id);
      } else if (cur.length >= MAX_INSTRUMENTS) {
        // Drop the oldest selection to make room.
        next = [...cur.slice(1), id];
      } else {
        next = [...cur, id];
      }
      saveInstrumentSelection(next);
      persistUiPrefs({ instruments: next });
      set({ instruments: next });
    },

    clock24h: _prefs.clock24h ?? true,
    setClock24h: (v) => {
      set({ clock24h: v });
      persistUiPrefs({ clock24h: v });
      // Keep the timeFormat module's state in sync so non-React
      // callers (instruments, taf rendering, alert text, audit
      // payloads) see the new format on their next call.
      setClock24hPref(v);
    },
  })),
);
